import * as loadjs from 'loadjs'
import { getQueryParam } from '../helpers/getQueryParam'
import IVideoConfig, { IPlaylistItem } from '../../typings/IVideoConfig'
import IVideoOptions from '../../typings/IVideoOptions'
import EventManager from '../eventManager/EventManager'
import StickyVideo, { MEDIAVINE_VIDEO_URL } from './StickyVideo'
import mvFetch from '../helpers/mvFetch'
import { LoadJsBundle } from '../enums/LoadJsBundle'
import Lazyable from '../lazyable/Lazyable'
import { VideoStatus } from '../enums/VideoConfig'
import { isTruthyAttribute } from '../helpers/DOM/isTruthyAttribute'
import isElementInDom from '../helpers/DOM/isElementInDom'

let placementLoaded = false
export async function setupPlacement(
  config: IVideoConfig,
  targetDiv: HTMLDivElement
): Promise<void> {
  if (!shouldRenderPlacement(config, targetDiv)) {
    return
  }

  // We save playerGroup on global window.MediavineVideo.playerGroup for two reasons.
  // 1.) We want to make sure that only one playerGroup is used for every video on a single page view.
  // 2.) We need to expose the playerGroup for WebWrapper to send it to GAM.

  if (!window.MediavineVideo) {
    // @ts-ignore
    window.MediavineVideo = {}
  }

  const group = StickyVideo.group
  // @ts-ignore
  window.MediavineVideo.playerGroup =
    // @ts-ignore
    window.MediavineVideo.playerGroup || group.name

  config.targetDiv = targetDiv

  const optsFromDiv = getDivOptions(targetDiv)
  const debugSrc = getQueryParam('debug_src') as string
  const playerSource = debugSrc || `${config.playerSource}/${group.version}`
  const videoSettings = window.$mediavine.web.model.videoSettings

  config.playerSource = debugSrc || playerSource
  config.playerVersion = group.version
  config.playerGroup = group.name

  EventManager.trigger(EventManager.events.initLauncher, config)

  // Avoid loading placement.js multiple times
  if (placementLoaded) {
    loadjs.ready(LoadJsBundle.placement, () => {
      if (window?.MediavineVideo?.placement?.render) {
        window.MediavineVideo.placement.render(config, optsFromDiv)
        StickyVideo.insertVideoHeadline(targetDiv, videoSettings)
      }
    })
  } else {
    placementLoaded = true
    loadjs(`${playerSource}/placement.js`, LoadJsBundle.placement)
    loadjs.ready(LoadJsBundle.placement, () => {
      if (window.MediavineVideo) {
        window.MediavineVideo.placement.render(config, optsFromDiv)
        StickyVideo.insertVideoHeadline(targetDiv, videoSettings)
      }
    })
  }
}

export function getDivOptions(
  optionsDiv: HTMLDivElement
): Partial<IVideoOptions> {
  if (!optionsDiv) {
    return {}
  }

  const callbacksValue = optionsDiv.getAttribute('data-callbacks')
  const callbacks = callbacksValue ? window[callbacksValue] : {}

  return {
    aspectRatio: optionsDiv.getAttribute('data-ratio') || undefined,
    autoInsert: isTruthyAttribute(optionsDiv.getAttribute('data-autoInsert')),
    disableJsonLD: isTruthyAttribute(
      optionsDiv.getAttribute('data-disable-jsonld')
    ),
    height: Number(optionsDiv.getAttribute('data-height')) || 0,
    isAutomobile: isTruthyAttribute(optionsDiv.getAttribute('data-automobile')),
    isAutoplay: isTruthyAttribute(optionsDiv.getAttribute('data-autoplay')),
    isMuted: isTruthyAttribute(optionsDiv.getAttribute('data-muted')),
    sticky: isTruthyAttribute(optionsDiv.getAttribute('data-sticky')),
    title: optionsDiv.getAttribute('data-title') || undefined,
    volume: Number(optionsDiv.getAttribute('data-volume')) || 0,
    width: Number(optionsDiv.getAttribute('data-width')) || 0,
    videoStartTime: Number(optionsDiv.getAttribute('data-start-time')) || 0,

    permalink: optionsDiv.getAttribute('data-permalink') || undefined,
    srcPoster: optionsDiv.getAttribute('data-srcPoster') || undefined,
    srcHls: optionsDiv.getAttribute('data-srcHls') || undefined,
    srcHlsH265: optionsDiv.getAttribute('data-srcHlsH265') || undefined,
    srcDash: optionsDiv.getAttribute('data-srcDash') || undefined,

    onAdStarted: callbacks.onAdStarted,
    onEnded: callbacks.onEnded,
    onError: callbacks.onError,
    onViewable: callbacks.onViewable
  }
}

function shouldRenderPlacement(
  config: IVideoConfig,
  targetDiv: HTMLDivElement
): boolean {
  if (!targetDiv) {
    console.error('video target div is missing.')
    return false
  }

  if (!config?.video?.status || config.video.status !== VideoStatus.live) {
    console.error('no video to play')

    // NOTE: one side effect here is the trade off of pulling this logic into separate function
    targetDiv.style.display = 'none'
    return false
  }

  if (!isElementInDom(targetDiv)) {
    return false
  }

  return true
}

export function objectContainsType(
  thingToCheck: Object,
  typeIs: string
): boolean {
  const objectKeys = Object.keys(thingToCheck)

  return objectKeys.reduce(
    (alreadyFound: boolean, currentKey) =>
      alreadyFound ||
      (currentKey === '@type' && thingToCheck[currentKey] === typeIs) ||
      (typeof thingToCheck === 'object' &&
        objectContainsType(thingToCheck[currentKey], typeIs)),
    false
  )
}

export function jsonStringHasType(jsonString: string, typeIs: string): boolean {
  try {
    const parsedJson = JSON.parse(jsonString)
    return (
      typeof parsedJson === 'object' && objectContainsType(parsedJson, typeIs)
    )
  } catch (e) {
    return false
  }
}

export function scriptsContainsLdJsonOfType(typeIs: string): boolean {
  const pageScripts = document.getElementsByTagName('script')
  const allScripts = Array.from(pageScripts)
  const ldjsonScripts = allScripts.filter(
    (scriptElement) => scriptElement.type === 'application/ld+json'
  )
  const itemListAlreadyPresent = ldjsonScripts.reduce(
    (foundItemList, jsonScript) =>
      foundItemList || jsonStringHasType(jsonScript.text, typeIs),
    false
  )

  return itemListAlreadyPresent
}

// LD+JSON needs to be rendered on page load to ensure it is indexed by Google.
export async function loadDataAndWriteLdJsonForTargetDiv(
  targetDiv: HTMLDivElement
) {
  const videoId = StickyVideo.getVideoIdFromTargetDiv(targetDiv)
  const playlistId = StickyVideo.getPlaylistIdFromTargetDiv(targetDiv)
  const configs = StickyVideo.videoConfigs
  const slug = window.$mediavine.web.model.slug
  const optsFromDiv = getDivOptions(targetDiv)

  // If both exist (they shouldn't), we'll go with the video over the playlist.
  if (videoId) {
    if (!configs[videoId]) {
      const videoRequest = mvFetch.fetchJson(
        `https://${MEDIAVINE_VIDEO_URL}/videos/v2/${videoId}.json`,
        'GET'
      ) as Promise<IVideoConfig>

      const upnextRequest = mvFetch.fetchJson(
        `https://${MEDIAVINE_VIDEO_URL}/playlists/v1/${slug}/upnext-raw.json`,
        'GET'
      ) as Promise<IPlaylistItem[]>

      configs[videoId] = Promise.all([videoRequest, upnextRequest])
        .then(([videoData, upnextData]) => {
          const allUpNextVideos = upnextData || []
          const filteredUpNextVideos = allUpNextVideos.filter(
            (video) => video.slug != videoData.video.slug
          )
          videoData.playlist = filteredUpNextVideos
          return videoData
        })
        .then((config) => StickyVideo.addSiteData(config))
        .catch((err) => ({ meta: {}, site: {}, video: {} } as IVideoConfig))

      // need global information about: is there any other @type: VideoObject on the page?
      StickyVideo.haveWrittenMajorSchema =
        StickyVideo.haveWrittenMajorSchema ||
        scriptsContainsLdJsonOfType('VideoObject')
      if (!optsFromDiv.disableJsonLD && !StickyVideo.haveWrittenMajorSchema) {
        StickyVideo.haveWrittenMajorSchema = true
        createLDJson(await configs[videoId])
      }
    }
  } else if (playlistId) {
    if (!configs[playlistId]) {
      configs[playlistId] = mvFetch
        .fetchJson(
          `https://${MEDIAVINE_VIDEO_URL}/playlists/v1/${slug}/${playlistId}.json`,
          'GET'
        )
        .then((config) => StickyVideo.addSiteData(config))
        .catch((err) => ({ meta: {}, site: {}, video: {} } as IVideoConfig))
      // have we written before? if not, continue
      StickyVideo.haveWrittenMajorSchema =
        StickyVideo.haveWrittenMajorSchema ||
        scriptsContainsLdJsonOfType('ItemList')
      if (!optsFromDiv.disableJsonLD && !StickyVideo.haveWrittenMajorSchema) {
        StickyVideo.haveWrittenMajorSchema = true
        createLDJson(await configs[playlistId])
      }
    }
  }
}

export async function setupPlacementForTargetDiv(targetDiv: HTMLDivElement) {
  const videoId = StickyVideo.getVideoIdFromTargetDiv(targetDiv)
  const playlistId = StickyVideo.getPlaylistIdFromTargetDiv(targetDiv)
  const configs = StickyVideo.videoConfigs

  // loadDataAndWriteLdJsonForTargetDiv won't do anything if it has
  // already been called for this targetDiv.
  await loadDataAndWriteLdJsonForTargetDiv(targetDiv)

  // calculate how much longer we should wait until it's safe to insert the video
  const timeout = Lazyable.contentDisabledFor

  // wait until it's safe to insert/hoist videos
  setTimeout(async () => {
    // If both exist (they shouldn't), we'll go with the video over the playlist.
    if (videoId) {
      const config = await configs[videoId]
      setupPlacement({ ...config }, targetDiv)
    } else if (playlistId) {
      const config = await configs[playlistId]
      setupPlacement({ ...config }, targetDiv)
    }
  }, timeout)
}

export function createLDJson(config: IVideoConfig): void {
  const script = document.createElement('script')
  const head = document.head || document.getElementsByTagName('head')[0]
  const meta = JSON.stringify(config.meta)

  script.className = 'mediavine-video__ld-json'
  script.setAttribute('type', 'application/ld+json')

  try {
    script.appendChild(document.createTextNode(meta))
    head.appendChild(script)
  } catch (e) {
    script.text = meta
    head.appendChild(script)
  }
}
